import { useState } from 'react'
import { CheckCircle2, ChevronRight, Loader2, XCircle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Modal } from '@/components/ui'

interface StepDebugModalProps {
  open: boolean
  itemCode: string
  onClose: () => void
}

interface StepResult {
  step: string
  request?: { method?: string; path?: string; body?: unknown }
  response?: unknown
  error?: string
  httpStatus?: number | null
  sapResponse?: unknown
  exists?: boolean
  sapItem?: unknown
  body?: Record<string, unknown>
  templateUsed?: boolean
  priceListNo?: number | null
  SalesPrice?: number | null
}

type StepKey = 'check' | 'build' | 'create' | 'update' | 'patch-price'

interface StepRow {
  key: StepKey
  label: string
  description: string
  pending: 'idle' | 'running' | 'done' | 'error'
  result?: StepResult
}

const INITIAL_STEPS: StepRow[] = [
  { key: 'check', label: '1. Existenz prüfen', description: 'GET /Items?$filter=ItemCode eq …', pending: 'idle' },
  { key: 'build', label: '2. Create-Body bauen', description: 'Lokal: Template + Defaults + User-Werte (ohne Request)', pending: 'idle' },
  { key: 'create', label: '3a. Item anlegen', description: 'POST /Items mit dem oben gebauten Body', pending: 'idle' },
  { key: 'update', label: '3b. Item updaten', description: 'PATCH /Items(\'<code>\') mit Stammdaten', pending: 'idle' },
  { key: 'patch-price', label: '4. Preis setzen', description: 'PATCH /Items(\'<code>\') mit ItemPrices', pending: 'idle' },
]

export function StepDebugModal({ open, itemCode, onClose }: StepDebugModalProps) {
  const [steps, setSteps] = useState<StepRow[]>(INITIAL_STEPS)
  // editable bodies for create/update/patch-price
  const [createBody, setCreateBody] = useState<string>('')
  const [updateBody, setUpdateBody] = useState<string>(JSON.stringify({ ItemName: '', SalesUnit: 'Stk' }, null, 2))
  const [pricePatchBody, setPricePatchBody] = useState<string>(JSON.stringify({ ItemPrices: [{ PriceList: 1, Price: 0 }] }, null, 2))

  function updateStep(key: StepKey, patch: Partial<StepRow>) {
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  }

  async function runStep(key: StepKey) {
    updateStep(key, { pending: 'running', result: undefined })
    try {
      const params = new URLSearchParams({ step: key })
      let body: unknown = undefined
      if (key === 'create') body = JSON.parse(createBody || '{}')
      if (key === 'update') body = JSON.parse(updateBody || '{}')
      if (key === 'patch-price') body = JSON.parse(pricePatchBody || '{}')

      const res = await apiClient.post(
        `/articles/by-code/${encodeURIComponent(itemCode)}/push-step?${params}`,
        body !== undefined ? { body } : {},
      )
      const data = res.data as StepResult
      updateStep(key, { pending: data?.error ? 'error' : 'done', result: data })

      // Wenn 'build' fertig ist, Body als Default in das create-Feld
      if (key === 'build' && data.body) {
        setCreateBody(JSON.stringify(data.body, null, 2))
      }
      if (key === 'build' && data.priceListNo && data.SalesPrice !== null && data.SalesPrice !== undefined) {
        setPricePatchBody(JSON.stringify({ ItemPrices: [{ PriceList: data.priceListNo, Price: data.SalesPrice }] }, null, 2))
      }
    } catch (err) {
      const axErr = err as { response?: { data?: unknown }; message?: string }
      updateStep(key, {
        pending: 'error',
        result: {
          step: key,
          error: axErr?.message ?? String(err),
          sapResponse: axErr?.response?.data ?? null,
        },
      })
    }
  }

  function reset() {
    setSteps(INITIAL_STEPS.map((s) => ({ ...s, pending: 'idle', result: undefined })))
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={`Step-Debug: SAP-Push für „${itemCode}"`}
      description="Jeder Schritt wird einzeln und manuell ausgeführt. Request-Body und volle SAP-Response sind sichtbar."
      size="xl"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={reset}>Zurücksetzen</Button>
          <Button variant="secondary" onClick={onClose}>Schließen</Button>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
        {steps.map((s) => (
          <StepCard
            key={s.key}
            step={s}
            createBody={createBody}
            updateBody={updateBody}
            pricePatchBody={pricePatchBody}
            onCreateBodyChange={setCreateBody}
            onUpdateBodyChange={setUpdateBody}
            onPricePatchBodyChange={setPricePatchBody}
            onRun={() => runStep(s.key)}
          />
        ))}
      </div>
    </Modal>
  )
}

function StepCard({
  step,
  createBody,
  updateBody,
  pricePatchBody,
  onCreateBodyChange,
  onUpdateBodyChange,
  onPricePatchBodyChange,
  onRun,
}: {
  step: StepRow
  createBody: string
  updateBody: string
  pricePatchBody: string
  onCreateBodyChange: (v: string) => void
  onUpdateBodyChange: (v: string) => void
  onPricePatchBodyChange: (v: string) => void
  onRun: () => void
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {step.pending === 'idle' && <ChevronRight className="w-4 h-4 text-gray-400" />}
          {step.pending === 'running' && <Loader2 className="w-4 h-4 text-brand-600 animate-spin" />}
          {step.pending === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          {step.pending === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-small font-medium text-gray-900">{step.label}</p>
              <p className="text-caption text-gray-500">{step.description}</p>
            </div>
            <Button variant={step.pending === 'idle' ? 'primary' : 'secondary'} size="sm" onClick={onRun} loading={step.pending === 'running'}>
              Ausführen
            </Button>
          </div>

          {(step.key === 'create' || step.key === 'update' || step.key === 'patch-price') && (
            <div className="mt-2">
              <label className="text-caption text-gray-600 block mb-0.5">Request-Body (JSON, editierbar)</label>
              <textarea
                value={
                  step.key === 'create' ? createBody
                  : step.key === 'update' ? updateBody
                  : pricePatchBody
                }
                onChange={(e) => {
                  if (step.key === 'create') onCreateBodyChange(e.currentTarget.value)
                  else if (step.key === 'update') onUpdateBodyChange(e.currentTarget.value)
                  else onPricePatchBodyChange(e.currentTarget.value)
                }}
                rows={6}
                className="w-full font-mono text-caption border border-gray-200 rounded p-2"
                spellCheck={false}
              />
            </div>
          )}

          {step.result && (
            <div className="mt-2 space-y-1.5">
              {step.result.error ? (
                <div className="rounded border border-red-200 bg-red-50 p-2">
                  <p className="text-caption font-medium text-red-900">Fehler</p>
                  <p className="text-small text-red-900">{step.result.error}</p>
                  {step.result.httpStatus !== undefined && step.result.httpStatus !== null && (
                    <p className="text-caption text-red-700 mt-0.5">HTTP {step.result.httpStatus}</p>
                  )}
                  {step.result.sapResponse !== undefined && step.result.sapResponse !== null && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-caption text-red-700">SAP-Originalantwort</summary>
                      <pre className="mt-1 p-2 bg-white border border-red-200 rounded text-caption font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(step.result.sapResponse, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ) : (
                <details className="rounded border border-emerald-200 bg-emerald-50 p-2" open>
                  <summary className="cursor-pointer text-caption font-medium text-emerald-900">Erfolg</summary>
                  {step.result.request && (
                    <div className="mt-1">
                      <p className="text-caption text-emerald-900">Request</p>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all">
                        {step.result.request.method} {step.result.request.path}
                        {step.result.request.body !== undefined ? `\n${JSON.stringify(step.result.request.body, null, 2)}` : ''}
                      </pre>
                    </div>
                  )}
                  {step.result.body && (
                    <div className="mt-1">
                      <p className="text-caption text-emerald-900">Berechneter Body{step.result.templateUsed ? ' (aus Template + Defaults)' : ' (nur Defaults, kein Template)'}</p>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(step.result.body, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.result.exists !== undefined && (
                    <p className="mt-1 text-small text-emerald-900">
                      {step.result.exists ? 'Item existiert in SAP.' : 'Item existiert NICHT in SAP.'}
                    </p>
                  )}
                  {step.result.response !== undefined && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-caption text-emerald-700">SAP-Response</summary>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(step.result.response, null, 2)}
                      </pre>
                    </details>
                  )}
                  {step.result.sapItem !== undefined && step.result.sapItem !== null && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-caption text-emerald-700">SAP-Item-Daten</summary>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all max-h-48 overflow-auto">
                        {JSON.stringify(step.result.sapItem, null, 2)}
                      </pre>
                    </details>
                  )}
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
